import {
  ProductAnalytics,
  type AnalyticsSink,
  type ProductAnalyticsContext,
  type ProductAnalyticsRecording,
  type ProductEvent,
  type ProductEventEnvelope,
} from "../productAnalytics";
import { type AnalyticsConsent } from "./analyticsConsent";
import {
  WebAnalyticsRuntimePolicy,
  WebAnalyticsRuntimePolicyRefresher,
} from "./analyticsRuntimePolicy";
import {
  createPageMemoryGuestAnalyticsStateStorage,
  type GuestAnalyticsStateStorage,
} from "./guestAnalyticsStateStorage";

export interface WebAnalyticsIdentityContext {
  guestBrowserIdentity: string;
  guestAnalyticsSessionIdentity: number;
}

export interface WebAnalyticsProvider {
  activate(): Promise<void>;
  record(
    envelope: ProductEventEnvelope,
    identity: WebAnalyticsIdentityContext,
  ): void;
  deactivate(): void;
}

export interface WebAnalyticsParticipation {
  readonly current: AnalyticsConsent;
  set(consent: Exclude<AnalyticsConsent, "unknown">): void;
  subscribe(listener: (consent: AnalyticsConsent) => void): () => void;
}

interface GuestAnalyticsStateRecord {
  version: 1;
  guestBrowserIdentity: string;
  issuedAt: number;
  expiresAt: number;
  sessionStartedAt: number;
  lastActivityAt: number;
}

export interface WebProductAnalyticsRuntimeOptions {
  context: ProductAnalyticsContext;
  localPolicy: {
    explicitlyEnabled: boolean;
    serverZone: string;
  };
  participation: WebAnalyticsParticipation;
  provider?: WebAnalyticsProvider;
  loadPolicy: () => Promise<unknown>;
  scheduleRepeating: (intervalMs: number, action: () => void) => () => void;
  subscribeToActiveSession: (action: () => void) => () => void;
  refreshIntervalMs?: number;
  eventDependencies?: {
    createEventId: () => string;
    now: () => Date;
  };
  guestAnalyticsStorage?: GuestAnalyticsStateStorage;
  guestAnalyticsDependencies?: {
    createGuestBrowserIdentity: () => string;
    now: () => Date;
  };
}

const guestBrowserIdentityLifetimeMs = 365 * 24 * 60 * 60 * 1_000;
const guestAnalyticsSessionTimeoutMs = 30 * 60 * 1_000;
const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const guestAnalyticsStateFields = [
  "expiresAt",
  "guestBrowserIdentity",
  "issuedAt",
  "lastActivityAt",
  "sessionStartedAt",
  "version",
];

const defaultGuestBrowserIdentity = (): string => crypto.randomUUID();

const isTimestamp = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

const parseGuestAnalyticsState = (
  serialized: string | null,
  now: number,
): GuestAnalyticsStateRecord | undefined => {
  if (!serialized) return undefined;
  try {
    const value: unknown = JSON.parse(serialized);
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return undefined;
    }
    const record = value as Record<string, unknown>;
    if (
      Object.keys(record).sort().join(",") !==
      guestAnalyticsStateFields.join(",")
    ) {
      return undefined;
    }
    if (
      record.version !== 1 ||
      typeof record.guestBrowserIdentity !== "string" ||
      !uuidPattern.test(record.guestBrowserIdentity) ||
      !isTimestamp(record.issuedAt) ||
      !isTimestamp(record.expiresAt) ||
      !isTimestamp(record.sessionStartedAt) ||
      !isTimestamp(record.lastActivityAt) ||
      record.expiresAt - record.issuedAt !== guestBrowserIdentityLifetimeMs ||
      record.issuedAt > record.sessionStartedAt ||
      record.sessionStartedAt > record.lastActivityAt ||
      record.lastActivityAt > now ||
      now >= record.expiresAt
    ) {
      return undefined;
    }
    return {
      version: 1,
      guestBrowserIdentity: record.guestBrowserIdentity,
      issuedAt: record.issuedAt,
      expiresAt: record.expiresAt,
      sessionStartedAt: record.sessionStartedAt,
      lastActivityAt: record.lastActivityAt,
    };
  } catch {
    return undefined;
  }
};

/**
 * Owns the complete Web collection lifecycle behind the feature-facing
 * Product Analytics recording interface.
 */
export class WebProductAnalyticsRuntime
  implements ProductAnalyticsRecording, WebAnalyticsParticipation
{
  private readonly policy: WebAnalyticsRuntimePolicy;
  private readonly refresher: WebAnalyticsRuntimePolicyRefresher;
  private readonly analytics: ProductAnalytics;
  private started = false;
  private providerActive = false;
  private lifecycleGeneration = 0;
  private activation: Promise<void> | undefined;
  private stopParticipationSubscription: (() => void) | undefined;
  private stopPolicySubscription: (() => void) | undefined;
  private readonly guestAnalyticsStorage: GuestAnalyticsStateStorage;
  private readonly createGuestBrowserIdentity: () => string;
  private readonly guestAnalyticsNow: () => Date;

  constructor(private readonly options: WebProductAnalyticsRuntimeOptions) {
    this.guestAnalyticsStorage =
      options.guestAnalyticsStorage ??
      createPageMemoryGuestAnalyticsStateStorage();
    this.createGuestBrowserIdentity =
      options.guestAnalyticsDependencies?.createGuestBrowserIdentity ??
      defaultGuestBrowserIdentity;
    this.guestAnalyticsNow =
      options.guestAnalyticsDependencies?.now ??
      options.eventDependencies?.now ??
      (() => new Date());
    this.policy = new WebAnalyticsRuntimePolicy({
      environment: options.context.environment,
      ...options.localPolicy,
    });
    this.refresher = new WebAnalyticsRuntimePolicyRefresher({
      policy: this.policy,
      load: options.loadPolicy,
      refreshIntervalMs: options.refreshIntervalMs ?? 300_000,
      scheduleRepeating: options.scheduleRepeating,
      subscribeToActiveSession: options.subscribeToActiveSession,
    });
    const sink: AnalyticsSink = {
      record: (envelope) => this.deliver(envelope),
    };
    this.analytics = new ProductAnalytics(
      sink,
      options.context,
      options.eventDependencies,
    );
  }

  get current(): AnalyticsConsent {
    return this.options.participation.current;
  }

  set(consent: Exclude<AnalyticsConsent, "unknown">): void {
    // Publish withdrawal before clearing shared identity state so another tab
    // cannot observe the cleared record and mint a replacement in between.
    this.options.participation.set(consent);
    if (consent === "denied") this.clearGuestAnalyticsState();
  }

  subscribe(listener: (consent: AnalyticsConsent) => void): () => void {
    return this.options.participation.subscribe(listener);
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.stopParticipationSubscription = this.options.participation.subscribe(
      (consent) => {
        if (consent === "denied") this.clearGuestAnalyticsState();
        this.reconcileProvider();
      },
    );
    this.stopPolicySubscription = this.policy.subscribeToAvailability(() =>
      this.reconcileProvider(),
    );

    if (this.isLocallyConfigured) {
      this.refresher.start();
    }
    if (this.current === "denied") this.clearGuestAnalyticsState();
    this.reconcileProvider();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.stopParticipationSubscription?.();
    this.stopPolicySubscription?.();
    this.stopParticipationSubscription = undefined;
    this.stopPolicySubscription = undefined;
    this.refresher.stop();
    this.policy.reset();
    this.invalidateProvider();
  }

  record(event: ProductEvent): void {
    if (!this.started) return;
    this.analytics.record(event);
  }

  private deliver(envelope: ProductEventEnvelope): void {
    if (!this.canCollect || !this.policy.allows(envelope)) return;
    const identity = this.admitGuestAnalyticsActivity();
    if (!identity) return;
    const generation = this.lifecycleGeneration;
    void this.ensureProviderActivated()
      .then((active) => {
        if (
          !active ||
          generation !== this.lifecycleGeneration ||
          !this.canCollect ||
          !this.policy.allows(envelope)
        ) {
          return;
        }
        try {
          this.options.provider?.record(envelope, identity);
        } catch {
          // Provider delivery must never affect the product action.
        }
      })
      .catch(() => undefined);
  }

  private admitGuestAnalyticsActivity():
    | WebAnalyticsIdentityContext
    | undefined {
    try {
      const now = this.guestAnalyticsNow().getTime();
      if (!isTimestamp(now)) return undefined;
      const stored = parseGuestAnalyticsState(
        this.guestAnalyticsStorage.read(),
        now,
      );
      const browserIdentity = stored?.guestBrowserIdentity;
      const guestBrowserIdentity =
        browserIdentity ?? this.createGuestBrowserIdentity();
      if (!uuidPattern.test(guestBrowserIdentity)) return undefined;

      const sessionStartedAt =
        stored && now - stored.lastActivityAt <= guestAnalyticsSessionTimeoutMs
          ? stored.sessionStartedAt
          : now;
      const state: GuestAnalyticsStateRecord = stored
        ? { ...stored, sessionStartedAt, lastActivityAt: now }
        : {
            version: 1,
            guestBrowserIdentity,
            issuedAt: now,
            expiresAt: now + guestBrowserIdentityLifetimeMs,
            sessionStartedAt,
            lastActivityAt: now,
          };
      this.guestAnalyticsStorage.write(JSON.stringify(state));
      if (this.current !== "granted") {
        this.clearGuestAnalyticsState();
        return undefined;
      }
      return {
        guestBrowserIdentity,
        guestAnalyticsSessionIdentity: sessionStartedAt,
      };
    } catch {
      // Identity and storage failures omit observation, never product behavior.
      return undefined;
    }
  }

  private clearGuestAnalyticsState(): void {
    try {
      this.guestAnalyticsStorage.clear();
    } catch {
      // Opt-out remains authoritative even if browser storage is unavailable.
    }
  }

  private reconcileProvider(): void {
    if (this.canCollect) {
      void this.ensureProviderActivated();
      return;
    }
    this.invalidateProvider();
  }

  private async ensureProviderActivated(): Promise<boolean> {
    if (!this.canCollect) return false;
    if (this.providerActive) return true;
    if (this.activation) {
      await this.activation;
      return this.providerActive;
    }

    const provider = this.options.provider;
    if (!provider) return false;
    const generation = this.lifecycleGeneration;
    const activation = Promise.resolve()
      .then(() => provider.activate())
      .then(() => {
        if (generation === this.lifecycleGeneration && this.canCollect) {
          this.providerActive = true;
          return;
        }
        this.safeDeactivateProvider();
      })
      .catch(() => undefined);
    this.activation = activation;
    await activation;
    if (this.activation === activation) this.activation = undefined;
    return this.providerActive;
  }

  private invalidateProvider(): void {
    this.lifecycleGeneration += 1;
    const providerMayRetainState =
      this.providerActive || Boolean(this.activation);
    this.providerActive = false;
    if (providerMayRetainState) this.safeDeactivateProvider();
  }

  private safeDeactivateProvider(): void {
    try {
      this.options.provider?.deactivate();
    } catch {
      // Withdrawal and runtime controls must never affect product behavior.
    }
  }

  private get isLocallyConfigured(): boolean {
    return Boolean(this.options.provider && this.policy.locallyEligible);
  }

  private get canCollect(): boolean {
    return (
      this.started &&
      this.current === "granted" &&
      this.isLocallyConfigured &&
      this.policy.collectionAvailable
    );
  }
}
